import { Anchor, Badge, Button, Checkbox, Group, NumberInput, Stack, Text, Textarea, TextInput } from '@mantine/core';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { additionalDetailsStepMessages } from './additional-details-step-messages';
import styles from './AdditionalDetailsStep.module.css';
import { ServicesGrid } from '../services-grid/ServicesGrid';

import type { FreightBookingFormState, WizardReferenceItem, WizardShipmentServices } from '../../freight-booking-wizard-types';
import type { ServicesGridField } from '../services-grid/ServicesGrid';

const SHIPMENT_SERVICE_FIELDS: ServicesGridField<WizardShipmentServices>[] = [
  { key: 'isSortAndSegregate', label: additionalDetailsStepMessages.sortAndSegregate },
  { key: 'isProtectFromFreeze', label: additionalDetailsStepMessages.protectFromFreeze },
];

export type AdditionalDetailsStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
};

export const AdditionalDetailsStep = ({ formState, onFormChange }: AdditionalDetailsStepProps) => {
  const intl = useIntl();

  const handleHandlingChargeEnabled = (checked: boolean) =>
    onFormChange({ ...formState, handlingCharge: { ...formState.handlingCharge, isEnabled: checked } });

  const handleHandlingChargeValue = (value: number | string) =>
    onFormChange({
      ...formState,
      handlingCharge: { ...formState.handlingCharge, value: typeof value === 'string' ? parseFloat(value) || 0 : value },
    });

  const handleReferencesEnabled = (checked: boolean) =>
    onFormChange({ ...formState, references: { ...formState.references, isEnabled: checked } });

  const handleReferenceChange = (id: string, field: keyof WizardReferenceItem, value: string) =>
    onFormChange({
      ...formState,
      references: {
        ...formState.references,
        items: formState.references.items.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
      },
    });

  const handleAddReference = () =>
    onFormChange({
      ...formState,
      references: {
        ...formState.references,
        items: [...formState.references.items, { id: crypto.randomUUID(), type: '', value: '' }],
      },
    });

  const handleRemoveReference = (id: string) =>
    onFormChange({
      ...formState,
      references: { ...formState.references, items: formState.references.items.filter((item) => item.id !== id) },
    });

  return (
    <Stack data-testid='additional-details-step' gap='md'>
      <Text fw={600}>{intl.formatMessage(additionalDetailsStepMessages.shipmentServicesHeader)}</Text>
      <ServicesGrid
        fields={SHIPMENT_SERVICE_FIELDS}
        onChange={(next) => onFormChange({ ...formState, shipmentServices: next })}
        value={formState.shipmentServices}
      />

      <Checkbox
        checked={formState.handlingCharge.isEnabled}
        data-testid='add-handling-charge'
        label={intl.formatMessage(additionalDetailsStepMessages.addHandlingCharge)}
        onChange={(event) => handleHandlingChargeEnabled(event.currentTarget.checked)}
      />
      {formState.handlingCharge.isEnabled && (
        <NumberInput
          allowNegative={false}
          data-testid='handling-charge-value'
          label={intl.formatMessage(additionalDetailsStepMessages.handlingChargeLabel)}
          min={0}
          onChange={handleHandlingChargeValue}
          prefix='$ '
          value={formState.handlingCharge.value}
        />
      )}

      <Checkbox
        checked={formState.references.isEnabled}
        data-testid='add-references'
        label={intl.formatMessage(additionalDetailsStepMessages.addReferences)}
        onChange={(event) => handleReferencesEnabled(event.currentTarget.checked)}
      />
      {formState.references.isEnabled && (
        <Stack gap='sm'>
          {formState.references.items.map((item) => (
            <Group align='flex-end' gap='sm' key={item.id}>
              <TextInput
                className={styles.referenceField}
                data-testid={`reference-type-${item.id}`}
                label={intl.formatMessage(additionalDetailsStepMessages.referenceTypeLabel)}
                onChange={(event) => handleReferenceChange(item.id, 'type', event.currentTarget.value)}
                placeholder={intl.formatMessage(additionalDetailsStepMessages.referenceTypePlaceholder)}
                value={item.type}
              />
              <TextInput
                className={styles.referenceField}
                data-testid={`reference-value-${item.id}`}
                label={intl.formatMessage(additionalDetailsStepMessages.referenceValueLabel)}
                onChange={(event) => handleReferenceChange(item.id, 'value', event.currentTarget.value)}
                placeholder={intl.formatMessage(additionalDetailsStepMessages.referenceValuePlaceholder)}
                value={item.value}
              />
              <Button
                color='red'
                data-testid={`remove-reference-${item.id}`}
                onClick={() => handleRemoveReference(item.id)}
                variant='ghost'
              >
                {intl.formatMessage(additionalDetailsStepMessages.removeReference)}
              </Button>
            </Group>
          ))}
          <Anchor component='button' data-testid='add-reference' onClick={handleAddReference} size='sm' type='button'>
            {intl.formatMessage(additionalDetailsStepMessages.addReference)}
          </Anchor>
        </Stack>
      )}

      <Textarea
        data-testid='pickup-instructions'
        label={intl.formatMessage(additionalDetailsStepMessages.pickupInstructionsLabel)}
        onChange={(event) => onFormChange({ ...formState, pickupInstructions: event.currentTarget.value })}
        value={formState.pickupInstructions}
      />
      <Textarea
        data-testid='delivery-instructions'
        label={intl.formatMessage(additionalDetailsStepMessages.deliveryInstructionsLabel)}
        onChange={(event) => onFormChange({ ...formState, deliveryInstructions: event.currentTarget.value })}
        value={formState.deliveryInstructions}
      />
      <Textarea
        data-testid='handling-instructions'
        label={intl.formatMessage(additionalDetailsStepMessages.handlingInstructionsLabel)}
        onChange={(event) => onFormChange({ ...formState, handlingInstructions: event.currentTarget.value })}
        value={formState.handlingInstructions}
      />
    </Stack>
  );
};

export type AdditionalDetailsStepSummaryProps = {
  formState: FreightBookingFormState;
  currencyCode: string;
};

export const AdditionalDetailsStepSummary = ({ formState, currencyCode }: AdditionalDetailsStepSummaryProps) => {
  const intl = useIntl();

  const selectedServices = [
    formState.shipmentServices.isSortAndSegregate ? intl.formatMessage(additionalDetailsStepMessages.sortAndSegregate) : null,
    formState.shipmentServices.isProtectFromFreeze ? intl.formatMessage(additionalDetailsStepMessages.protectFromFreeze) : null,
  ].filter(Boolean) as string[];

  const hasServices = selectedServices.length > 0;
  const hasHandlingCharge = formState.handlingCharge.isEnabled;
  const activeReferences = formState.references.isEnabled
    ? formState.references.items.filter((item) => item.type.trim() !== '' || item.value.trim() !== '')
    : [];
  const hasReferences = activeReferences.length > 0;

  const sections = [hasServices, hasHandlingCharge, hasReferences];
  const lastSectionIndex = sections.lastIndexOf(true);

  return (
    <div className={styles.summary} data-testid='additional-details-summary'>
      {hasServices && (
        <div className={clsx(styles.summarySection, lastSectionIndex === 0 && styles.summarySectionLast)}>
          <span className={styles.summarySectionLabel}>
            {intl.formatMessage(additionalDetailsStepMessages.summaryShipmentServicesLabel)}
          </span>
          <div className={styles.tags}>
            {selectedServices.map((service) => (
              <Badge color='light' key={service} size='lg' variant='filled'>
                {service}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {hasHandlingCharge && (
        <div className={clsx(styles.summarySection, lastSectionIndex === 1 && styles.summarySectionLast)}>
          <span className={styles.summarySectionLabel}>{intl.formatMessage(additionalDetailsStepMessages.summaryHandlingChargeLabel)}</span>
          <span className={styles.summarySectionValue}>
            {intl.formatNumber(formState.handlingCharge.value, { style: 'currency', currency: currencyCode })}
          </span>
        </div>
      )}
      {hasReferences && (
        <div className={clsx(styles.summarySection, lastSectionIndex === 2 && styles.summarySectionLast)}>
          <span className={styles.summarySectionLabel}>{intl.formatMessage(additionalDetailsStepMessages.summaryReferencesLabel)}</span>
          {activeReferences.map((item) => (
            <span className={styles.summarySectionValue} key={item.id}>
              {intl.formatMessage(additionalDetailsStepMessages.summaryReferenceItem, { type: item.type, value: item.value })}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
